import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";


// IMPORTANT: keep your existing API_URL import exactly how you already do it elsewhere
const API_URL = process.env.REACT_APP_API_URL;
// If you have shared UI components (Button/Input/etc) use them.
// Otherwise this file uses simple HTML controls styled with Tailwind.
function Field({ label, help, required, children }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-gray-900">
          {label} {required ? <span className="text-red-600">*</span> : null}
        </div>
      </div>
      {children}
      {help ? <div className="text-xs text-gray-500">{help}</div> : null}
    </div>
  );
}

function normalizeSchema(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((x) => x && typeof x === "object")
    .map((x) => ({
      key: String(x.key || "").trim(),
      label: String(x.label || x.key || "").trim(),
      type: (x.type || "text").toLowerCase(),
      required: Boolean(x.required),
      help: String(x.help || "").trim(),
      // optional: options for select
      options: Array.isArray(x.options) ? x.options : undefined,
    }))
    .filter((x) => x.key && x.label);
}

function coerceDefaultValue(type) {
  if (type === "number" || type === "currency") return "";
  if (type === "date") return "";
  if (type === "textarea") return "";
  if (type === "select") return "";
  if (type === "checkbox") return false;
  return "";
}

export default function TrusteeDocGenerate() {
  const { caseId } = useParams(); // route param should be :caseId (your BankruptcyCase.case_id)
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [templatesError, setTemplatesError] = useState("");

  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const selectedTemplate = useMemo(
    () => templates.find((t) => String(t.id) === String(selectedTemplateId)),
    [templates, selectedTemplateId]
  );

  const schema = useMemo(
    () => normalizeSchema(selectedTemplate?.field_schema),
    [selectedTemplate]
  );

  const [payload, setPayload] = useState({});
  const [checklistName, setChecklistName] = useState("");
  const [checklistDescription, setChecklistDescription] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState("");
  const [error, setError] = useState("");

  const [generated, setGenerated] = useState(null);
  // generated example:
  // { packet_id, checklist_item_id, generated_docx, generated_pdf }

  // Replace this with your real token getter (whatever you already use)
  const firmToken = useMemo(() => {
    return localStorage.getItem("accessToken") || localStorage.getItem("token") || "";
  }, []);

  useEffect(() => {
    let alive = true;

    async function loadTemplates() {
      setLoadingTemplates(true);
      setTemplatesError("");
      try {
        const res = await fetch(`${API_URL}/firm/trustee-doc-templates/`, {
          headers: {
            Authorization: `Bearer ${firmToken}`,
          },
        });

        if (!res.ok) {
          const txt = await res.text();
          throw new Error(`Failed to load templates (${res.status}): ${txt}`);
        }

        const data = await res.json();
        // DRF pagination or not
        const rows = Array.isArray(data) ? data : Array.isArray(data.results) ? data.results : [];
        if (alive) setTemplates(rows);
      } catch (e) {
        if (alive) setTemplatesError(e.message || "Failed to load templates");
      } finally {
        if (alive) setLoadingTemplates(false);
      }
    }

    if (firmToken) loadTemplates();
    else {
      setLoadingTemplates(false);
      setTemplatesError("Missing firm token. Please log in again.");
    }

    return () => {
      alive = false;
    };
  }, [firmToken]);

  // When template changes, initialize payload + checklist defaults
  useEffect(() => {
    setToast("");
    setError("");
    setGenerated(null);

    if (!selectedTemplate) {
      setPayload({});
      setChecklistName("");
      setChecklistDescription("");
      return;
    }

    const nextPayload = {};
    const s = normalizeSchema(selectedTemplate.field_schema);
    for (const f of s) {
      nextPayload[f.key] = coerceDefaultValue(f.type);
    }

    setPayload(nextPayload);

    // Defaults for checklist naming — tweak as desired
    const baseName = selectedTemplate.name || "Trustee Document";
    setChecklistName(baseName);
    setChecklistDescription(
      selectedTemplate.default_checklist_description ||
        "Please download the document, complete it, and upload the signed/completed version here."
    );
  }, [selectedTemplateId]); // eslint-disable-line react-hooks/exhaustive-deps

  function updatePayload(key, value) {
    setPayload((prev) => ({ ...prev, [key]: value }));
  }

  function validateBeforeGenerate() {
    if (!caseId) return "Missing case id in URL.";
    if (!selectedTemplateId) return "Please select a template.";
    for (const f of schema) {
      if (f.required) {
        const v = payload?.[f.key];
        const empty =
          v === null ||
          v === undefined ||
          (typeof v === "string" && v.trim() === "") ||
          (typeof v === "number" && Number.isNaN(v));
        if (empty) return `Please complete: ${f.label}`;
      }
    }
    if (!checklistName || !String(checklistName).trim()) return "Checklist item name is required.";
    return "";
  }

  async function handleGenerate() {
    setToast("");
    setError("");

    const msg = validateBeforeGenerate();
    if (msg) {
      setError(msg);
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(
        `${API_URL}/firm/trustee-doc-templates/${selectedTemplateId}/generate/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${firmToken}`,
          },
          body: JSON.stringify({
            case_id: caseId,
            payload,
            checklist_name: checklistName,
            checklist_description: checklistDescription,
            is_required: true,
          }),
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          data?.detail ||
            (typeof data === "object" ? JSON.stringify(data) : "Failed to generate")
        );
      }

      setGenerated({
        packet_id: data.packet_id,
        checklist_item_id: data.checklist_item_id,
        generated_docx: data.generated_docx,
        generated_pdf: data.generated_pdf,
      });

      setToast("Generated document successfully. A checklist item was created for the client upload.");
    } catch (e) {
      setError(e.message || "Generate failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDownloadDocx() {
    setToast("");
    setError("");

    if (!generated?.packet_id) return;

    try {
      const res = await fetch(
        `${API_URL}/firm/trustee-doc-packets/${generated.packet_id}/presign/`,
        {
          headers: {
            Authorization: `Bearer ${firmToken}`,
          },
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail || "Failed to create download link");
      }

      if (!data?.url) throw new Error("No URL returned");
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError(e.message || "Download failed");
    }
  }

  async function handleDownloadPdf() {
    setToast("");
    setError("");

    if (!generated?.packet_id) return;

    try {
      const res = await fetch(
        `${API_URL}/firm/trustee-doc-packets/${generated.packet_id}/presign/?kind=pdf`,
        {
          headers: {
            Authorization: `Bearer ${firmToken}`,
          },
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail || "Failed to create PDF download link");
      }

      if (!data?.url) throw new Error("No URL returned");
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError(e.message || "Download failed");
    }
  }

  function handleOpenDocuments() {
    // Adjust this path to your actual firm case documents route
    navigate(`/dashboard/cases/${caseId}/documents`);
  }

  return (
      <div className="space-y-8">
        <div className="bg-white rounded shadow p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Generate Trustee Document</h1>
              <p className="mt-1 text-sm text-gray-600">
                Select a template, fill in any custom fields, and generate a DOCX for the client to complete and upload.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleGenerate}
                disabled={submitting || loadingTemplates || !selectedTemplateId}
                className={`px-4 py-2 rounded text-white font-semibold ${
                  submitting || loadingTemplates || !selectedTemplateId
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-700"
                }`}
              >
                {submitting ? "Generating…" : "Generate"}
              </button>

              {generated?.packet_id ? (
                <>
                  <button
                    onClick={handleDownloadDocx}
                    className="px-4 py-2 rounded font-semibold border border-gray-300 bg-white hover:bg-gray-50 text-gray-900"
                  >
                    Download DOCX
                  </button>
                  <button
                    onClick={handleDownloadPdf}
                    className="px-4 py-2 rounded font-semibold border border-gray-300 bg-white hover:bg-gray-50 text-gray-900"
                  >
                    Download PDF
                  </button>
                </>
              ) : null}

              {generated?.checklist_item_id ? (
                <button
                  onClick={handleOpenDocuments}
                  className="px-4 py-2 rounded font-semibold border border-gray-300 bg-white hover:bg-gray-50 text-gray-900"
                >
                  Open Documents
                </button>
              ) : null}
            </div>
          </div>

          {toast ? (
            <div className="mt-4 px-4 py-3 rounded-md bg-green-50 border border-green-400 text-green-700">
              {toast}
            </div>
          ) : null}

          {error ? (
            <div className="mt-4 px-4 py-3 rounded-md bg-red-50 border border-red-400 text-red-700">
              {error}
            </div>
          ) : null}
        </div>

        <section className="bg-white rounded shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Template & Fields</h2>
            <div className="text-sm text-gray-500">Case: {caseId}</div>
          </div>

          {loadingTemplates ? (
            <div className="text-gray-600">Loading templates…</div>
          ) : templatesError ? (
            <div className="text-red-700 bg-red-50 border border-red-200 rounded p-3">
              {templatesError}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-1 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="text-sm font-semibold text-gray-900">Select Template</div>
                <div className="mt-2">
                  <select
                    value={selectedTemplateId}
                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 bg-white text-gray-900"
                  >
                    <option value="">— Choose —</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name || t.id}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedTemplate ? (
                  <div className="mt-4 text-sm text-gray-700 space-y-1">
                    <div className="font-semibold text-gray-900">{selectedTemplate.name}</div>
                    {selectedTemplate.description ? (
                      <div className="text-gray-600">{selectedTemplate.description}</div>
                    ) : null}
                    <div className="text-xs text-gray-500 mt-2">
                      {schema.length === 0
                        ? "No custom fields. Generate will use case data only."
                        : `${schema.length} custom field(s) required for this template.`}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="lg:col-span-2 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-gray-900">Custom Fields</div>
                  {schema.length ? (
                    <div className="text-xs text-gray-500">Fields come from template schema</div>
                  ) : null}
                </div>

                {selectedTemplateId && schema.length === 0 ? (
                  <div className="mt-3 text-sm text-gray-600">
                    This template has no custom fields. You can generate immediately.
                  </div>
                ) : null}

                {schema.length ? (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {schema.map((f) => {
                      const v = payload?.[f.key];

                      if (f.type === "textarea") {
                        return (
                          <div key={f.key} className="md:col-span-2">
                            <Field label={f.label} help={f.help} required={f.required}>
                              <textarea
                                value={v ?? ""}
                                onChange={(e) => updatePayload(f.key, e.target.value)}
                                className="w-full border border-gray-300 rounded px-3 py-2 bg-white text-gray-900 min-h-[90px]"
                              />
                            </Field>
                          </div>
                        );
                      }

                      if (f.type === "select") {
                        const opts = Array.isArray(f.options) ? f.options : [];
                        return (
                          <Field key={f.key} label={f.label} help={f.help} required={f.required}>
                            <select
                              value={v ?? ""}
                              onChange={(e) => updatePayload(f.key, e.target.value)}
                              className="w-full border border-gray-300 rounded px-3 py-2 bg-white text-gray-900"
                            >
                              <option value="">— Select —</option>
                              {opts.map((o, idx) => {
                                const val =
                                  typeof o === "string" ? o : String(o?.value ?? o?.key ?? idx);
                                const lab =
                                  typeof o === "string" ? o : String(o?.label ?? o?.name ?? val);
                                return (
                                  <option key={`${f.key}-${val}-${idx}`} value={val}>
                                    {lab}
                                  </option>
                                );
                              })}
                            </select>
                          </Field>
                        );
                      }

                      if (f.type === "checkbox") {
                        return (
                          <Field key={f.key} label={f.label} help={f.help} required={f.required}>
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={Boolean(v)}
                                onChange={(e) => updatePayload(f.key, e.target.checked)}
                              />
                              <span className="text-sm text-gray-700">Yes</span>
                            </label>
                          </Field>
                        );
                      }

                      const inputType =
                        f.type === "number" || f.type === "currency"
                          ? "number"
                          : f.type === "date"
                          ? "date"
                          : "text";

                      return (
                        <Field key={f.key} label={f.label} help={f.help} required={f.required}>
                          <input
                            type={inputType}
                            value={v ?? ""}
                            onChange={(e) => updatePayload(f.key, e.target.value)}
                            className="w-full border border-gray-300 rounded px-3 py-2 bg-white text-gray-900"
                          />
                        </Field>
                      );
                    })}
                  </div>
                ) : null}

                <div className="mt-6 border-t border-gray-200 pt-4">
                  <div className="text-sm font-semibold text-gray-900">Checklist Item (Client Upload Target)</div>
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Checklist Item Name" required>
                      <input
                        value={checklistName}
                        onChange={(e) => setChecklistName(e.target.value)}
                        className="w-full border border-gray-300 rounded px-3 py-2 bg-white text-gray-900"
                        placeholder="Tax Refund Affidavit (2023)"
                      />
                    </Field>

                    <Field label="Checklist Description">
                      <input
                        value={checklistDescription}
                        onChange={(e) => setChecklistDescription(e.target.value)}
                        className="w-full border border-gray-300 rounded px-3 py-2 bg-white text-gray-900"
                        placeholder="Instructions for the client…"
                      />
                    </Field>
                  </div>

                  {generated?.checklist_item_id ? (
                    <div className="mt-4 text-sm text-gray-700 bg-white border border-gray-200 rounded p-3">
                      <div className="font-semibold text-gray-900">Generated</div>
                      <div className="mt-1">
                        Checklist Item ID: <span className="font-mono">{generated.checklist_item_id}</span>
                      </div>
                      <div>
                        Packet ID: <span className="font-mono">{generated.packet_id}</span>
                      </div>
                      {generated.generated_docx ? (
                        <div className="text-xs text-gray-500 mt-1 break-all">
                          DOCX stored as: <span className="font-mono">{generated.generated_docx}</span>
                        </div>
                      ) : null}
                      {generated.generated_pdf ? (
                        <div className="text-xs text-gray-500 mt-1 break-all">
                          PDF stored as: <span className="font-mono">{generated.generated_pdf}</span>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
  );
}